import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@material-ui/core';

import { InventoryAdjustmentApi } from 'api/inventoryAdjustment';
import { WarehouseInventoryV2Api } from 'api/warehouseInventoryV2';
import { Notifications } from 'shared';
import { SearchableSelect } from 'shared/components/SearchableSelect';

const inventoryAdjustmentApi = new InventoryAdjustmentApi();
const warehouseApiV2 = new WarehouseInventoryV2Api();

function CycleCountAdjustByMagnitudeAction({
    actualCases,
    actualUnits,
    cycleCountEventId,
    expectedCases,
    expectedUnits,
    initialReason,
    locationId,
    locationName,
    lotId,
    onCancel,
    onSuccess,
    palletIds = [],
    unitAdjustment,
}) {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [reasons, setReasons] = useState([]);
    const [reasonsLoading, setReasonsLoading] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);
    const [selectedPalletId, setSelectedPalletId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const showPalletSelector = palletIds && palletIds.length > 1;

    useEffect(() => {
        if (!(onSuccess instanceof Function)) {
            console.error('AdjustByMagnitudeAction initialized incorrectly: onSuccess is not a function');
        }
        if (!(onCancel instanceof Function)) {
            console.error('AdjustByMagnitudeAction initialized incorrectly: onCancel is not a function');
        }
    }, [onSuccess, onCancel]);

    useEffect(() => {
        queryAdjustReasons();
    }, []);

    const queryAdjustReasons = async () => {
        setReasonsLoading(true);

        const result = await warehouseApiV2.getAdjustReasons(lotId);
        if (result?.status) {
            setReasons(result?.data?.reasons ?? []);
        } else {
            setNotification({ text: result.msg, level: 'error' });
        }

        setReasonsLoading(false);
    };

    const reasonOptions = useMemo(() => reasons.map((reason) => ({ label: reason, value: reason })), [reasons]);

    const palletOptions = useMemo(
        () => palletIds.map((palletId) => ({ label: `Pallet ${palletId}`, value: palletId })),
        [palletIds],
    );

    const handleSelectedReasonChange = useCallback((newValue) => {
        setSelectedReason(newValue?.value);
    }, []);

    const handleSelectedPalletChange = useCallback((newValue) => {
        setSelectedPalletId(newValue?.value);
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!selectedReason) {
            setNotification({ text: 'Please select a reason', level: 'error' });
            return;
        }

        if (showPalletSelector && !selectedPalletId) {
            setNotification({ text: 'Please select a pallet', level: 'error' });
            return;
        }

        setSubmitting(true);

        const result = await inventoryAdjustmentApi.resolveUnitDiscrepancyWithMagnitudeAdjustment(
            cycleCountEventId,
            unitAdjustment,
            selectedReason,
            showPalletSelector ? selectedPalletId : palletIds.length === 1 ? palletIds[0] : null,
        );
        if (result.status) {
            onSuccess('Adjustment completed successfully');
        } else {
            setNotification({ text: result.msg, level: 'error' });
        }

        setSubmitting(false);
    };

    const getAdjustmentText = () => {
        if (unitAdjustment === 0) {
            return 'No adjustment needed';
        }

        let unitAdjustmentText = '';

        if (unitAdjustment > 0) {
            unitAdjustmentText = `up by ${unitAdjustment.toLocaleString()} unit${unitAdjustment !== 1 ? 's' : ''}`;
        } else if (unitAdjustment < 0) {
            unitAdjustmentText = `down by ${Math.abs(unitAdjustment).toLocaleString()} unit${Math.abs(unitAdjustment) !== 1 ? 's' : ''}`;
        }

        return `Submitting will adjust ${unitAdjustmentText}`;
    };

    return (
        <>
            <Notifications options={notification} />

            <Box mb={3}>
                <Typography variant="body1" style={{ marginBottom: 16 }}>
                    {lotId && (
                        <div>
                            <strong>Lot ID: </strong>
                            {lotId}
                        </div>
                    )}
                    {locationName && (
                        <div>
                            <strong>Location: </strong>
                            {locationName}
                        </div>
                    )}
                    {palletIds.length === 1 && (
                        <div>
                            <strong>Pallet: </strong>
                            {palletIds[0]}
                        </div>
                    )}
                    <div>
                        <strong>Expected: </strong>
                        {expectedCases?.toLocaleString()} cases, {expectedUnits?.toLocaleString()} units
                    </div>
                    <div>
                        <strong>Actual: </strong>
                        {actualCases?.toLocaleString()} cases, {actualUnits?.toLocaleString()} units
                    </div>
                    <div>{getAdjustmentText()}</div>
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                    {showPalletSelector && (
                        <SearchableSelect
                            onChange={handleSelectedPalletChange}
                            options={palletOptions}
                            label="Select Pallet"
                            required
                        />
                    )}

                    <SearchableSelect
                        onChange={handleSelectedReasonChange}
                        options={reasonOptions}
                        loading={reasonsLoading}
                        label="Reason"
                        initialValue={initialReason}
                        required
                    />

                    <Box display="flex" justifyContent="center" alignItems="center" style={{ gap: 20 }}>
                        <Button onClick={onCancel} variant="outlined">
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                            {submitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Box>
                </Box>
            </form>
        </>
    );
}

export { CycleCountAdjustByMagnitudeAction };
